/**
 * useSocket - Hook personnalisé pour la gestion WebSocket
 * Remplace le polling par des connexions temps réel
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { io } from 'socket.io-client';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Singleton pour éviter les connexions multiples
let socketInstance = null;
let connectionCount = 0;

export function useSocket(customerId, options = {}) {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const socketRef = useRef(null);
  const listenersRef = useRef({});

  // Connexion au serveur
  useEffect(() => {
    if (!customerId) return;

    // Utiliser le singleton ou créer une nouvelle connexion
    if (!socketInstance) {
      socketInstance = io(API_URL, {
        auth: { customerId },
        path: '/api/socket.io',
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });
    }

    socketRef.current = socketInstance;
    connectionCount++;

    const socket = socketRef.current;

    // Événements de connexion
    socket.on('connect', () => {
      console.log('🔌 WebSocket connecté');
      setIsConnected(true);
    });

    socket.on('disconnect', () => {
      console.log('🔌 WebSocket déconnecté');
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Erreur WebSocket:', error);
      setIsConnected(false);
    });

    // Événements de présence
    socket.on('user_online', (data) => {
      setOnlineUsers(prev => [...new Set([...prev, data.customerId])]);
    });

    socket.on('user_offline', (data) => {
      setOnlineUsers(prev => prev.filter(id => id !== data.customerId));
    });

    // Événements de typing
    socket.on('typing', (data) => {
      setTypingUsers(prev => ({
        ...prev,
        [data.matchId]: data.isTyping 
          ? { ...prev[data.matchId], [data.customerId]: true }
          : Object.fromEntries(
              Object.entries(prev[data.matchId] || {}).filter(([k]) => k !== data.customerId)
            )
      }));

      // Auto-clear après 3 secondes
      if (data.isTyping) {
        setTimeout(() => {
          setTypingUsers(prev => ({
            ...prev,
            [data.matchId]: Object.fromEntries(
              Object.entries(prev[data.matchId] || {}).filter(([k]) => k !== data.customerId)
            )
          }));
        }, 3000);
      }
    });

    // 🆕 TEMPS RÉEL: Événements profil, match, guides
    socket.on('profile_updated', (data) => {
      console.log('📢 Profile updated via WebSocket:', data);
      window.dispatchEvent(new CustomEvent('ws:profile_updated', { detail: data }));
    });

    socket.on('new_match', (data) => {
      console.log('💕 New match via WebSocket:', data);
      window.dispatchEvent(new CustomEvent('ws:new_match', { detail: data }));
    });

    socket.on('guide_message', (data) => {
      console.log('🌙 Guide message via WebSocket:', data);
      window.dispatchEvent(new CustomEvent('ws:guide_message', { detail: data }));
    });

    socket.on('swipe_received', (data) => {
      console.log('👀 Swipe received via WebSocket:', data);
      window.dispatchEvent(new CustomEvent('ws:swipe_received', { detail: data }));
    });

    socket.on('notification', (data) => {
      console.log('🔔 Notification via WebSocket:', data);
      window.dispatchEvent(new CustomEvent('ws:notification', { detail: data }));
    });

    // Cleanup
    return () => {
      connectionCount--;
      if (connectionCount === 0 && socketInstance) {
        socketInstance.disconnect();
        socketInstance = null;
      }
    };
  }, [customerId]);

  // Rejoindre un match (room de chat)
  const joinMatch = useCallback((matchId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('join_match', { matchId });
    }
  }, []);

  // Quitter un match
  const leaveMatch = useCallback((matchId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave_match', { matchId });
    }
  }, []);

  // Envoyer un message
  const sendMessage = useCallback((matchId, text, options = {}) => {
    return new Promise((resolve, reject) => {
      if (!socketRef.current?.connected) {
        reject(new Error('Non connecté'));
        return;
      }

      socketRef.current.emit('send_message', {
        matchId,
        text,
        isEphemeral: options.isEphemeral || false,
        replyToId: options.replyToId || null
      }, (response) => {
        if (response.error) {
          reject(new Error(response.error));
        } else {
          resolve(response);
        }
      });
    });
  }, []);

  // Indicateur de frappe
  const startTyping = useCallback((matchId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing_start', { matchId });
    }
  }, []);

  const stopTyping = useCallback((matchId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing_stop', { matchId });
    }
  }, []);

  // Marquer message comme lu
  const markAsRead = useCallback((messageId, matchId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('message_read', { messageId, matchId });
    }
  }, []);

  // Ajouter une réaction
  const addReaction = useCallback((messageId, matchId, reaction) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('add_reaction', { messageId, matchId, reaction });
    }
  }, []);

  // Check-in/out magasin
  const checkinStore = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('checkin_store', {});
    }
  }, []);

  const checkoutStore = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('checkout_store', {});
    }
  }, []);

  // Appels vidéo
  const callOffer = useCallback((targetCustomerId, matchId, offer) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('call_offer', { targetCustomerId, matchId, offer });
    }
  }, []);

  const callAnswer = useCallback((callerCustomerId, answer) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('call_answer', { callerCustomerId, answer });
    }
  }, []);

  const callReject = useCallback((callerCustomerId, reason = 'declined') => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('call_reject', { callerCustomerId, reason });
    }
  }, []);

  const callEnd = useCallback((partnerCustomerId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('call_end', { partnerCustomerId });
    }
  }, []);

  const sendIceCandidate = useCallback((targetCustomerId, candidate) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('ice_candidate', { targetCustomerId, candidate });
    }
  }, []);

  // Écouter un événement personnalisé
  const on = useCallback((event, callback) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback);
      listenersRef.current[event] = callback;
    }
    return () => {
      if (socketRef.current) {
        socketRef.current.off(event, callback);
        delete listenersRef.current[event];
      }
    };
  }, []);

  // Arrêter d'écouter un événement
  const off = useCallback((event) => {
    if (socketRef.current && listenersRef.current[event]) {
      socketRef.current.off(event, listenersRef.current[event]);
      delete listenersRef.current[event];
    }
  }, []);

  return {
    // État
    isConnected,
    onlineUsers,
    typingUsers,
    lastMessage,
    socket: socketRef.current,

    // Rooms
    joinMatch,
    leaveMatch,

    // Messagerie
    sendMessage,
    startTyping,
    stopTyping,
    markAsRead,
    addReaction,

    // Présence
    checkinStore,
    checkoutStore,

    // Appels vidéo
    callOffer,
    callAnswer,
    callReject,
    callEnd,
    sendIceCandidate,

    // Custom events
    on,
    off,
  };
}

export default useSocket;
